import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Alert, Spinner } from './shared';

export default function AdminInstrumentProfileEditor() {
    const { canonicalId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isNew = !canonicalId;

    const [cid, setCid] = useState(canonicalId || searchParams.get('canonical_id') || '');
    const [content, setContent] = useState('');
    const [contentType, setContentType] = useState('markdown');
    const [attributesText, setAttributesText] = useState('{}');
    const [rev, setRev] = useState(null);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        if (canonicalId) load();
    }, [canonicalId]);

    const load = async () => {
        try {
            setLoading(true);
            const profile = await adminApi.getInstrumentProfile(canonicalId);
            setContent(profile.content || '');
            setContentType(profile.content_type || 'markdown');
            setRev(profile.rev);
            setAttributesText(JSON.stringify({
                tags: profile.tags || [],
                sectors: profile.sectors || [],
                watchlist_priority: profile.watchlist_priority,
                ...(profile.attributes || {}),
            }, null, 2));
            setError(null);
        } catch (err) {
            if (err.message?.includes('404') || err.message?.includes('no profile')) {
                setError(null);
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!cid) { setError('canonical_id required (e.g. equity:HOSE:VNM)'); return; }
        let attrs;
        try { attrs = JSON.parse(attributesText); }
        catch (e) { setError('Invalid JSON in attributes'); return; }

        try {
            setSaving(true);
            await adminApi.upsertInstrumentProfile(cid, {
                content,
                content_type: contentType,
                attributes: attrs,
                expected_rev: rev,
            });
            setSuccessMessage('Profile saved (RAG re-index enqueued)');
            setTimeout(() => {
                navigate('/admin/instrument-profiles');
            }, 800);
        } catch (err) {
            setError(err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading..." center />;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <Button variant="ghost" size="xs" onClick={() => navigate('/admin/instrument-profiles')}>← Back</Button>
                    <h1 className="text-lg font-semibold text-gray-900 mt-1">
                        {isNew ? 'New Instrument Profile' : `Edit Profile: ${cid}`}
                    </h1>
                </div>
                <Button size="sm" disabled={saving} onClick={handleSave}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="space-y-4 max-w-4xl">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Canonical ID</label>
                    <input
                        type="text"
                        value={cid}
                        onChange={(e) => setCid(e.target.value)}
                        disabled={!isNew}
                        placeholder="equity:HOSE:VNM"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono disabled:bg-gray-50"
                    />
                    <p className="text-xs text-gray-400 mt-1">Format: <code>{'{asset_class}:{venue}:{symbol}'}</code></p>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Content Type</label>
                    <select
                        value={contentType}
                        onChange={(e) => setContentType(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                        <option value="markdown">Markdown</option>
                        <option value="json">JSON</option>
                        <option value="html">HTML</option>
                        <option value="plain">Plain</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Content</label>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows={18}
                        placeholder="# Instrument profile&#10;&#10;## Business summary&#10;..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Attributes (JSON)</label>
                    <textarea
                        value={attributesText}
                        onChange={(e) => setAttributesText(e.target.value)}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                        Common: <code>tags</code>, <code>sectors</code>, <code>watchlist_priority</code>, <code>freshness_overrides</code>.
                    </p>
                </div>

                {rev !== null && (
                    <p className="text-xs text-gray-500">Current rev: {rev} — optimistic lock will reject concurrent updates.</p>
                )}
            </div>
        </div>
    );
}
